import FilterListIcon from '@mui/icons-material/FilterList'
import { DatePicker } from '@mui/lab'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { BlurWrapper, Labels, Loader, MainContent, Sidebar } from 'components'
import { format, parseJSON } from 'date-fns'
import { COMPARE_RECRODS_GRAPH_COLORS } from 'helpers'
import { useEffect, useMemo, useRef, useState } from 'react'
import Plot from 'react-plotly.js'
import { useQueries, useQuery } from 'react-query'
import { api, apiConfig, useFeaturesContext } from 'services'

const EXEPMTED_INSTITUTES = [399]

const LinesFilters = ({
  options = [],
  onSelectedChanged,
  selectedOptions,
  isLoading,
  getOptionLabel = (option) => option.name
}) => {
  const handleSelectedChanged = (event, value) => {
    onSelectedChanged(value)
  }

  return (
    <Autocomplete
      size="small"
      multiple
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={selectedOptions}
      renderInput={(params) => <TextField {...params} label="Select Lines" />}
      onChange={handleSelectedChanged}
      filterSelectedOptions
      autoHighlight
      loading={isLoading}
      loadingText="Loading Lines..."
    />
  )
}

const ValidationsFilters = ({
  options = [],
  onSelectedChanged,
  selectedOptions
}) => {
  const handleSelectedChanged = (event, value) => {
    onSelectedChanged(value)
  }

  return (
    <Autocomplete
      size="small"
      multiple
      options={options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={selectedOptions}
      renderInput={(params) => (
        <TextField {...params} label="Select Validations" />
      )}
      onChange={handleSelectedChanged}
      filterSelectedOptions
      autoHighlight
    />
  )
}

const RecordsGraphPlot = ({
  recordsDetailedDataQueryResults = [],
  showLegends,
  downloadImageType = 'svg'
}) => {
  const { flags, overrides, getFlag } = useFeaturesContext()
  const [blurInfo, setBlurInfo] = useState(false)

  useEffect(() => {
    if (EXEPMTED_INSTITUTES.includes(apiConfig.currentApiInstitute)) {
      setBlurInfo(false)
      return
    }
    if (getFlag) {
      setBlurInfo(!getFlag('troubleshooting'))
    }
  }, [flags, overrides, getFlag])

  const isLoading = recordsDetailedDataQueryResults.some(
    (result) => result.isLoading
  )
  const successfulResults = recordsDetailedDataQueryResults.filter(
    (result) => result.isSuccess && result.data
  )

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress
          size={50}
          sx={{
            color: 'primary.main'
          }}
        />
      </Box>
    )
  }

  if (successfulResults.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Typography variant="body1">
          Unable to load graph data for the selected records.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      px={2}
    >
      <BlurWrapper doBlur={blurInfo} showCTAComponent={true} blurLevel={40}>
        <Plot
          data={successfulResults.map((result) => {
            return {
              ...result.data.cumulative,
              type: 'line',
              xaxis: 'x0',
              yaxis: 'y0',
              marker: {
                color: result.data.color
              }
            }
          })}
          useResizeHandler
          layout={{
            height: 350,
            grid: {
              rows: 1,
              columns: 1,
              pattern: 'independent'
            },
            margin: {
              t: 20,
              b: 35,
              l: 50,
              r: 20
            },
            legend: {
              orientation: 'h',
              yanchor: 'bottom',
              y: 1.4
            },
            showlegend: showLegends,
            xaxis: {
              title: 'Time (s)'
            },
            yaxis: {
              title: 'VitalMetric'
            }
          }}
          config={{
            toImageButtonOptions: {
              format: downloadImageType
            }
          }}
        />
        <Plot
          data={successfulResults.map((result, index) => {
            return {
              ...result.data.acceleration,
              type: 'scatter',
              hoverinfo: 'text',
              xaxis: 'x' + (index + 1),
              yaxis: 'y' + (index + 1),
              marker: {
                color: result.data.color
              }
            }
          })}
          useResizeHandler
          layout={{
            height:
              successfulResults.length * 350 -
              (successfulResults.length - 1) * 150,
            grid: {
              rows: successfulResults.length,
              columns: 1,
              pattern: 'independent'
            },
            margin: {
              t: 30,
              b: 50,
              l: 50,
              r: 20
            },
            legend: {
              orientation: 'h',
              yanchor: 'bottom',
              y: 1.04
            },
            showlegend: showLegends,
            xaxis: {
              title: 'Time (s)'
            },
            yaxis: {
              title: 'Acceleration (g)'
            },
            ...successfulResults.reduce(
              (acc, result, index) => ({
                ...acc,
                ['xaxis' + (index + 1)]: {
                  title: 'Time (s)'
                },
                ['yaxis' + (index + 1)]: {
                  title: 'Acceleration (g)'
                }
              }),
              {}
            )
          }}
          config={{
            toImageButtonOptions: {
              format: downloadImageType
            }
          }}
        />
      </BlurWrapper>
    </Box>
  )
}

export const CompareRecordsWorkspace = ({
  scope = 'single-org',
  instituteIds = '',
  headerContent = null,
  initialSelectedLines = []
}) => {
  const sidebarElm = useRef(null)
  const [showFilters, setShowFilters] = useState(true)
  const [selectedValidations, setSelectedValidations] = useState([])
  const [selectedLines, setSelectedLines] = useState(initialSelectedLines)
  const [selectedRecordsIds, setSelectedRecordsIds] = useState([])
  const [recordsDataGridPageSize, setRecordsDataGridPageSize] = useState(10)
  const [showLegends, setShowLegends] = useState(true)
  const [downLoadImageType, setDownLoadImageType] = useState('svg')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const previousInstituteIdsRef = useRef(instituteIds)

  const isCrossOrg = scope === 'cross-org'
  const lineIds = useMemo(
    () => selectedLines.map((line) => line.id).join(','),
    [selectedLines]
  )
  const validationIds = useMemo(
    () => selectedValidations.map((validation) => validation.id).join(','),
    [selectedValidations]
  )

  const recordsDataColumns = useMemo(() => {
    const columns = [
      {
        field: 'labels',
        headerName: 'Labels',
        sortable: false,
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const labels = params.getValue(params.id, 'labels')
          return (
            <Labels
              shownLabels={labels && labels.slice(0, 2)}
              hiddenLabels={labels && labels.slice(2)}
              display="flex"
              flexDirection="column"
              flexGrow={1}
              justifyContent="flex-start"
            />
          )
        }
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) => {
          return (
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.875rem',
                whiteSpace: 'normal'
              }}
            >
              {value ? format(parseJSON(value), 'yyyy-MM-dd HH:mm:ss') : 'NA'}
            </Typography>
          )
        }
      },
      {
        field: 'metrics',
        headerName: 'VMetric',
        flex: 1,
        minWidth: 100,
        renderCell: ({ value }) => {
          return (
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.875rem'
              }}
            >
              {value?.auc || 'NA'}
            </Typography>
          )
        }
      },
      {
        field: 'line',
        headerName: 'Line',
        flex: 1,
        minWidth: 140,
        renderCell: ({ value }) => {
          return (
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.875rem'
              }}
            >
              {value?.name || 'NA'}
            </Typography>
          )
        }
      }
    ]

    if (isCrossOrg) {
      columns.push({
        field: 'institute',
        headerName: 'Organization',
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => {
          return (
            <Typography
              variant="body1"
              sx={{
                fontSize: '0.875rem'
              }}
            >
              {value?.name || 'NA'}
            </Typography>
          )
        }
      })
    }

    columns.push({
      field: 'validation',
      headerName: 'Validation',
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => {
        return (
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.875rem'
            }}
          >
            {value?.name || 'NA'}
          </Typography>
        )
      }
    })

    return columns
  }, [isCrossOrg])

  const { data: lines, isLoading: isLinesLoading } = useQuery(
    ['compare-records-lines', scope, apiConfig.currentApiInstitute, instituteIds],
    async () => {
      if (isCrossOrg) {
        return api.crossOrg.lines.list({
          size: 9999,
          institute_ids: instituteIds,
          archived: false
        })
      }

      return api.lines.list({
        size: 9999
      })
    },
    {
      enabled: !isCrossOrg || Boolean(instituteIds)
    }
  )

  const {
    data: validations = [],
    isLoading: isValidationsLoading
  } = useQuery(
    ['compare-records-validations', scope, instituteIds, lineIds],
    async () => {
      const validationResponses = await Promise.all(
        selectedLines.map((line) => api.lines.getValidations(line.id))
      )

      return validationResponses
        .flat()
        .map((validation) => ({
          id: validation.validation_id,
          name: validation.validation_name,
          startDate: validation.validation_start_date
        }))
        .filter(
          (validation, index, self) =>
            index === self.findIndex((item) => item.id === validation.id)
        )
    },
    {
      enabled: selectedLines.length > 0
    }
  )

  const {
    data: recordsData,
    isLoading: isRecordsLoading,
    refetch: refetchRecordsData
  } = useQuery(
    [
      'compare-records-records',
      scope,
      instituteIds,
      lineIds,
      validationIds,
      startDate,
      endDate
    ],
    async () => {
      const params = {
        size: 9999,
        line_ids: lineIds || null,
        validation_ids: validationIds || null,
        date__gte: startDate || null,
        date__lt: endDate || null,
        ordering: '-date',
        exclude: false
      }

      if (isCrossOrg) {
        return api.crossOrg.records.list({
          ...params,
          institute_ids: instituteIds,
          is_processed: true
        })
      }

      return api.records.list(params)
    },
    {
      enabled: false
    }
  )

  const selectedRecords = useMemo(() => {
    if (!recordsData?.items?.length || selectedRecordsIds.length === 0) {
      return []
    }

    const selectedIds = new Set(selectedRecordsIds.map((id) => Number(id)))

    return recordsData.items.filter((record) => selectedIds.has(record.id))
  }, [recordsData, selectedRecordsIds])

  const recordsDetailedDataQueryResults = useQueries(
    selectedRecords.map((record, index) => {
      const recordName = isCrossOrg
        ? `${record?.line?.name || 'NA'}${
            record?.institute?.name ? ` (${record.institute.name})` : ''
          } - ${format(parseJSON(record?.date), 'd MMM yyyy HH:mm')}`
        : `${record?.line?.name || 'NA'} - ${format(
            parseJSON(record?.date),
            'd MMM yyyy HH:mm'
          )}`

      return {
        queryKey: ['compare-records-detail', scope, record.id],
        queryFn: async () => {
          const accelerationResponse = await api.records.acceleration(record.id)
          const cumulativeResponse = await api.records.cumulative(record.id)
          return {
            color: COMPARE_RECRODS_GRAPH_COLORS[index % 10],
            record,
            acceleration: {
              x: accelerationResponse.acceleration_time,
              y: accelerationResponse.acceleration,
              name: recordName,
              text: accelerationResponse.acceleration_time.map(
                (time, resultIndex) =>
                  `${recordName} ${accelerationResponse.acceleration[
                    resultIndex
                  ].toFixed(2)}g at ${time.toFixed(2)} seconds`
              ),
              maximum_acceleration: accelerationResponse.maximum_acceleration
            },
            cumulative: {
              x: cumulativeResponse.time,
              y: cumulativeResponse.cumulative_metric,
              name: recordName,
              text: cumulativeResponse.time.map(
                (time, resultIndex) =>
                  `${recordName} ${cumulativeResponse.cumulative_metric[
                    resultIndex
                  ].toFixed(2)}g at ${time.toFixed(2)} seconds`
              ),
              hoverinfo: 'text'
            }
          }
        }
      }
    })
  )

  useEffect(() => {
    setSelectedValidations([])
  }, [lineIds])

  useEffect(() => {
    if (isCrossOrg && previousInstituteIdsRef.current !== instituteIds) {
      setSelectedLines([])
    }
    previousInstituteIdsRef.current = instituteIds
  }, [isCrossOrg, instituteIds])

  useEffect(() => {
    setSelectedRecordsIds([])
  }, [scope, instituteIds, lineIds, validationIds, startDate, endDate])

  const handleShowRecordsButtonClick = () => {
    refetchRecordsData()
  }

  const handleShowLegendsSwitchChange = (event) => {
    setShowLegends(event.target.checked)
  }

  const handleDownloadImageTypeSelectChange = (event) => {
    setDownLoadImageType(event.target.value)
  }

  const getLineOptionLabel = (option) => {
    if (!isCrossOrg) {
      return option.name
    }

    return `${option.name}${
      option.institute ? ` (${option.institute.name})` : ''
    }`
  }

  return (
    <>
      <Sidebar ref={sidebarElm}>
        <Box
          display="flex"
          flexDirection="column"
          padding={1}
          justifyContent="center"
          gap={1}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Typography variant="h6">Filters</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Box>
          <Collapse in={showFilters}>
            <Box display="flex" flexDirection="column" gap={1}>
              <LinesFilters
                options={lines?.items || []}
                selectedOptions={selectedLines}
                onSelectedChanged={setSelectedLines}
                isLoading={isLinesLoading}
                getOptionLabel={getLineOptionLabel}
              />
              <ValidationsFilters
                options={validations}
                selectedOptions={selectedValidations}
                onSelectedChanged={setSelectedValidations}
                isLoading={isValidationsLoading}
              />
              <Box display="flex" flexDirection="row" gap={1}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue)
                  }}
                  disableFuture
                  disableMaskedInput={true}
                  inputFormat="dd MMM yyyy"
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                />
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue)
                  }}
                  disableFuture
                  disableMaskedInput={true}
                  inputFormat="dd MMM yyyy"
                  minDate={startDate || null}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                />
              </Box>
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  size="small"
                  disabled={selectedLines.length === 0}
                  onClick={handleShowRecordsButtonClick}
                >
                  Get Records
                </Button>
              </Box>
            </Box>
          </Collapse>
          <Divider orientation="horizontal" />
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="h6">Records</Typography>
            <DataGrid
              rows={recordsData?.items || []}
              autoHeight
              columns={recordsDataColumns}
              loading={isRecordsLoading}
              pageSize={recordsDataGridPageSize}
              onPageSizeChange={(newPageSize) =>
                setRecordsDataGridPageSize(newPageSize)
              }
              componentsProps={{
                pagination: {
                  labelRowsPerPage: 'Per Page'
                }
              }}
              rowsPerPageOptions={[10, 25, 50, 90]}
              checkboxSelection
              disableSelectionOnClick
              hideFooterSelectedRowCount
              onSelectionModelChange={(newSelectionModel) => {
                setSelectedRecordsIds(newSelectionModel)
              }}
            />
          </Box>
        </Box>
      </Sidebar>
      <MainContent sidebar="right">
        <Box display="flex" flexDirection="column" height="100%">
          {headerContent ? (
            <Box
              px={4}
              pt={4}
              pb={2}
              display="flex"
              flexDirection="column"
              gap={3}
            >
              {headerContent}
            </Box>
          ) : null}
          <Box
            display="flex"
            px={headerContent ? 4 : 2}
            justifyContent="space-between"
            alignItems="center"
            pt={headerContent ? 0 : 1}
          >
            <Typography variant="h6">Compare Records</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl>
                <InputLabel id="download-image-type-label">Image Type</InputLabel>
                <Select
                  size="small"
                  label="Image Type"
                  labelId="download-image-type-label"
                  value={downLoadImageType}
                  onChange={handleDownloadImageTypeSelectChange}
                  sx={{
                    height: 30,
                    width: 100
                  }}
                >
                  <MenuItem value="png">
                    <Typography
                      display="flex"
                      alignItems="center"
                      variant="body1"
                      fontSize={16}
                    >
                      PNG
                    </Typography>
                  </MenuItem>
                  <MenuItem value="svg">
                    <Typography
                      display="flex"
                      alignItems="center"
                      variant="body1"
                      fontSize={16}
                    >
                      SVG
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    onChange={handleShowLegendsSwitchChange}
                    checked={showLegends}
                  />
                }
                label="Show Graph Info"
              />
            </Box>
          </Box>
          {selectedRecordsIds.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="70%"
            >
              <Typography variant="body1">
                Please filter and select records to view data.
              </Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              height="100%"
              alignItems="center"
            >
              {recordsDetailedDataQueryResults.some(
                (result) => result.isLoading
              ) ? (
                <Loader />
              ) : (
                <RecordsGraphPlot
                  recordsDetailedDataQueryResults={
                    recordsDetailedDataQueryResults || []
                  }
                  showLegends={showLegends}
                  downloadImageType={downLoadImageType}
                />
              )}
            </Box>
          )}
        </Box>
      </MainContent>
    </>
  )
}

const CompareRecords = () => {
  return <CompareRecordsWorkspace />
}

export default CompareRecords
